/**
 * Auth gate — blocks UI rendering until authenticated.
 *
 * When AUTH_ENABLED=false: renders children immediately
 * When AUTH_ENABLED=true: shows loading/error states,
 *   redirects to Auth0 login if unauthenticated
 */
import { useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { AUTH_ENABLED } from './config'

interface Props {
  children: React.ReactNode
}

function AuthGateInner({ children }: Props) {
  const { isAuthenticated, isLoading, error, loginWithRedirect } = useAuth0()

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !error) {
      loginWithRedirect()
    }
  }, [isLoading, isAuthenticated, error, loginWithRedirect])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Authenticating...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center max-w-md p-6">
          <p className="text-destructive font-medium mb-2">Authentication Error</p>
          <p className="text-muted-foreground text-sm mb-4">{error.message}</p>
          <button
            onClick={() => loginWithRedirect()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-muted-foreground">Redirecting to login...</div>
      </div>
    )
  }

  return <>{children}</>
}

export default function AuthGate({ children }: Props) {
  if (!AUTH_ENABLED) {
    return <>{children}</>
  }

  return <AuthGateInner>{children}</AuthGateInner>
}
